import { useState, useEffect } from "react";

function useScrollAnim() {
  useEffect(() => {
    const els = document.querySelectorAll(".anim-fade-up, .anim-fade-left, .anim-fade-right, .anim-scale-in");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
          }
        });
      },
      { threshold: 0.1 }
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
}

export default function MessageEntry() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
    phone: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const [briefingData, setBriefingData] = useState({ name: "", email: "" });
  const [briefingSubmitted, setBriefingSubmitted] = useState(false);
  const [briefingSubmitting, setBriefingSubmitting] = useState(false);

  useScrollAnim();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name === "message") {
      if (value.length > 500) return;
      setCharCount(value.length);
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleBriefingChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setBriefingData((prev) => ({ ...prev, [name]: value }));
  };

  const handleBriefingSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setBriefingSubmitting(true);
    try {
      const params = new URLSearchParams();
      params.append("name", briefingData.name);
      params.append("email", briefingData.email);
      await fetch("https://readdy.ai/api/form/d76ge2kbmgf2o8mm76tg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      setBriefingSubmitted(true);
    } catch {
      // silent
    } finally {
      setBriefingSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (charCount > 500) return;
    setSubmitting(true);
    try {
      const params = new URLSearchParams();
      params.append("name", formData.name);
      params.append("email", formData.email);
      params.append("phone", formData.phone);
      params.append("message", formData.message);
      await fetch("https://readdy.ai/api/form/d75pkubq7u9ee9hce2fg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      setSubmitted(true);
    } catch {
      // silent
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="entry" className="py-20 bg-white relative overflow-hidden">
      {/* Floating accent image */}
      <img
        src="https://storage.readdy-site.link/project_files/b4049c21-524e-4360-bd0f-e8c5b9d42418/a8ac861b-3255-40ac-8afa-4150c90a7927_unnamed.png?v=efbb49a077cae0d415a8ee9ed8319b5f"
        alt=""
        className="absolute top-8 right-4 w-24 md:w-36 opacity-80 pointer-events-none animate-float-slow"
        style={{ "--rot": "-6deg" } as React.CSSProperties}
      />
      <div className="max-w-5xl mx-auto px-4 md:px-8">
        {/* Section header */}
        <div className="text-center mb-14 anim-fade-up">
          <div className="inline-block bg-[#00B050] text-white font-black text-sm px-6 py-2 rounded-full mb-4 tracking-widest border-4 border-black">
            MESSAGE &amp; ENTRY
          </div>
          <h2 className="text-2xl sm:text-4xl md:text-5xl font-black text-black whitespace-nowrap">
            一緒に<span className="text-[#00B050]">働きませんか？</span>
          </h2>
        </div>

        {/* Group photo + message */}
        <div className="flex flex-col lg:flex-row gap-10 items-center mb-16">
          <div className="w-full lg:w-[480px] shrink-0 relative anim-fade-left">
            <div className="absolute -inset-2 rounded-3xl border-4 border-[#00B050] rotate-1" />
            <img
              src="https://static.readdy.ai/image/cb8749fdfddc72e72c266efb2e07a84d/6bc4cb2da5895dcea3557fa05a390d67.jpeg"
              alt="トラックボーイズ 集合写真"
              className="relative z-10 w-full rounded-2xl object-cover object-top border-4 border-black"
              style={{ height: "340px" }}
            />
          </div>
          <div className="flex-1 anim-fade-right">
            <div className="bg-[#F0FFF5] border-4 border-[#00B050] rounded-3xl p-6 md:p-8"
              style={{ boxShadow: "6px 6px 0px #00B050" }}>
              <div className="flex items-center gap-2 mb-4">
                <i className="ri-message-3-fill text-[#00B050] text-2xl w-8 h-8 flex items-center justify-center"></i>
                <span className="font-black text-[#00B050] text-lg">トラックボーイズより</span>
              </div>
              <p className="text-gray-700 font-bold text-base md:text-lg leading-relaxed">
                僕たちは、自分たちの仕事の良いとこも悪いとこも
                <strong className="text-[#00B050]">全部知ってもらい</strong>、
                それでも一緒に働きたいと思ってくれる方を見つけるために、
                トラックボーイズを作っています。
              </p>
              <p className="text-gray-700 font-bold text-base md:text-lg leading-relaxed mt-4">
                もし、こんな感じの会社いいかもと思ってくれたなら嬉しいです。
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                {["楽しく働ける", "リアルを全公開", "仲間を募集中"].map((tag, i) => (
                  <span key={tag} className="bg-[#00B050] text-white font-black text-sm px-4 py-1 rounded-full border-2 border-black anim-scale-in" style={{ transitionDelay: `${i * 100}ms` }}>
                    ✓ {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* 配信動画説明会 */}
        <div className="mb-16 anim-fade-up anim-delay-200">
          <div className="border-4 border-[#FFD700] rounded-3xl p-6 md:p-10 bg-white relative"
            style={{ boxShadow: "8px 8px 0px #FFD700" }}>
            <div className="absolute -top-5 left-8">
              <div className="bg-[#FFD700] text-black font-black text-base px-5 py-2 rounded-full border-4 border-black animate-wiggle">
                🎬 配信動画説明会
              </div>
            </div>
            <div className="mt-4 flex flex-col lg:flex-row gap-8 items-center">
              <div className="w-full lg:w-[340px] shrink-0">
                <img
                  src="https://static.readdy.ai/image/cb8749fdfddc72e72c266efb2e07a84d/a821fa0e85c88d609222cd6a2e499b96.png"
                  alt="配信動画説明会"
                  className="w-full rounded-2xl object-cover object-top border-4 border-black"
                  style={{ height: "220px" }}
                />
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-black text-black mb-3">
                  入社しようかな〜と思ってる方へ
                </h3>
                <p className="text-gray-700 font-bold text-base leading-relaxed mb-2">
                  お給料から仕事内容まで、会社の全てを公開した<strong className="text-black">動画</strong>をご用意しています。
                </p>
                <p className="text-gray-700 font-bold text-base leading-relaxed mb-6">
                  興味のある方はお名前とメールアドレスを入力してください！動画のご案内をお送りします。
                </p>
                <div className="flex flex-col items-center gap-4 py-2">
                  <a
                    href="https://docs.google.com/forms/d/e/1FAIpQLSeJQ_wEGYDkCWm2rEulVYPXHV8FfI6unywMdzyjgaTRyKwPew/viewform"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full bg-[#FFD700] text-black font-black text-lg py-4 rounded-2xl border-4 border-black whitespace-nowrap cursor-pointer hover:scale-[1.02] transition-transform text-center block"
                    style={{ boxShadow: "4px 4px 0px #000" }}
                  >
                    🎬 配信動画説明会に応募する！
                  </a>
                  <p className="text-center text-xs text-gray-400 font-bold">
                    ※ Googleフォームへ移動します
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Entry */}
        <div className="border-4 border-[#00B050] rounded-3xl p-6 md:p-10 bg-white anim-fade-up anim-delay-300"
          style={{ boxShadow: "8px 8px 0px #00B050" }}>
          <div className="flex justify-center mb-4">
            <img
              src="https://public.readdy.ai/ai/img_res/4f96c41b-61fa-4311-a5da-6eac841c9d2b.png"
              alt="イラスト"
              className="w-40 h-40 object-contain"
            />
          </div>
          <h3 className="text-2xl md:text-3xl font-black text-black mb-2">
            エントリーはこちら
          </h3>
          <p className="text-gray-500 font-bold mb-8 text-sm">採用ページへ移動します</p>
          <div className="flex flex-col items-center gap-6 py-4">
            <p className="text-gray-700 font-bold text-base md:text-lg leading-relaxed text-center">
              一緒に働いてみたい！と思ってくれた方は、<br />
              下のボタンから採用ページへどうぞ！
            </p>
            <a
              href="https://lifebank.co.jp/recruit/"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full max-w-md bg-[#00B050] text-white font-black text-xl py-5 rounded-2xl border-4 border-black whitespace-nowrap cursor-pointer hover:scale-[1.02] transition-transform text-center block animate-pulse-scale"
              style={{ boxShadow: "5px 5px 0px #000" }}
            >
              🚛 エントリーする！
            </a>
            <p className="text-center text-xs text-gray-400 font-bold">
              ※ 採用ページ（lifebank.co.jp）へ移動します
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
